import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  reviewItemQuery,
  reviewListQuery,
  useDeleteRejectedUpload,
  useSubmitReviewResponse,
} from "@/portals/patient/api/patientQueries";
import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { Button } from "@/shared/components/button";
import { AppModal, Textarea } from "@/shared/components/dialog";
import { PageBackButton } from "@/shared/components/PageBackButton";
import { PageSpinner } from "@/shared/components/spinner";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { useOptionalUploadHistory } from "@/portals/patient/context/UploadHistoryContext";
import { cn } from "@/shared/lib/utils";
import { adaptMedication } from "@/features/patient/medications/utils/adaptMedication";
import {
  COMMENT_MAX_LENGTH,
  type ReviewAnswer,
  type ReviewItem,
} from "@/portals/patient/api/reviewItemsApi";
import { ISSUE_LABELS, ClinicianQuestionBadge } from "./medicationReviewBadges";
import { ReviewRecords, isPairItem } from "./ReviewConflictCard";
import { EFFECT_LABELS, outcomeActionLabel } from "./reviewOutcomeLabels";
import { PatientConversation, buildConversation } from "./PatientConversation";
import { IDENTITY_MESSAGES, IdentityReview } from "@/features/patient/human-review/components/IdentityReview";
import { CONFLICT_MESSAGES, ConflictPicker } from "@/features/patient/human-review/components/ConflictPicker";
import { ACTION_LABELS } from "@/features/patient/human-review/reviewActionLabels";
import { ConflictOptions } from "@/features/empi/ConflictOptions";

export const ANSWER_LABELS: Record<ReviewAnswer, string> = {
  CURRENT_MEDICINE: "This is my current medicine",
  NO_LONGER_TAKE: "This is an old medicine I no longer take",
  DOSE_CHANGED: "My dose changed",
  TAKE_BOTH: "I take both of these",
  NOT_RECOGNIZED: "I don't recognize this medicine",
  NOT_SURE: "I'm not sure",
  SKIPPED: "Skipped, let a clinician decide",
  REPLY: "Replied to your clinician",
  SAME_PERSON: "Yes, this is me",
  NOT_SAME_PERSON: "No, this isn't me",
  USE_THIS_VALUE: "You picked the value you think is right",
};

/** Why a medicine is asked about, in one plain sentence (the same box record and identity checks have). */
export const MEDICINE_REASON_TEXT: Partial<Record<ReviewItem["triggerType"], string>> = {
  UNRECOGNIZED_NAME: "We couldn't match this medicine name to a known medicine. Tell us about it so your clinician can confirm it.",
  RXNORM_REVIEW_BAND: "We found a likely match for this medicine but we're not sure it's right. Tell us if this is your medicine.",
  VENDOR_DISAGREEMENT: "Your records show this medicine differently, for example another dose or how often it's taken.",
  MISSING_INFORMATION: "Some details of this medicine are missing from your records.",
  LOW_CONFIDENCE_EXTRACTION: "We couldn't read this medicine clearly from your document.",
};

const OPTIONS: Exclude<ReviewAnswer, "SKIPPED" | "REPLY">[] = [
  "CURRENT_MEDICINE", "NO_LONGER_TAKE", "DOSE_CHANGED", "TAKE_BOTH", "NOT_RECOGNIZED", "NOT_SURE",
];

export const MESSAGES = {
  success: "Thank you. A clinician will review this.",
  replied: "Your reply has been sent to your clinician.",
  replyRequired: "Please type your reply to your clinician.",
  resolvedNoLongerTake: "Done. We've noted that you no longer take this medicine. Nothing else to do.",
  skipped: "Sent to your clinician without an answer. They'll decide.",
  alreadyAnswered: "You've already reviewed this record.",
  alreadyReviewed: "This medicine was already reviewed by your clinician.",
  notFound: "This review item isn't available.",
  loadFailed: "Couldn't load this review item. Please try again.",
};

const BRAND_BUTTON =
  "!h-10 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-6 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white disabled:!cursor-not-allowed disabled:!opacity-60";

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** A review item's records as Medications carrying their role, so ReviewRecords lays them out like the card. */
/** A review item's records as Medication values (shared with the Human Review Queue). */
export function toMedications(item: ReviewItem) {
  return item.records.map((r) =>
    adaptMedication({
      id: r.id,
      resourceType: "MedicationRequest",
      sourceVendor: r.sourceVendor,
      uploadId: null,
      confidenceScore: 1,
      needsReview: false,
      reviewReason: null,
      createdAt: r.createdAt,
      updatedAt: r.createdAt,
      fields: r.fields,
      rxcui: r.rxcui ?? null,
      review: { status: "UNDER_REVIEW", reviewItemId: item.id, recordRole: r.recordRole, triggerType: item.triggerType },
      origin: null,
    }),
  );
}

/** The item's Patient Conversation: the clinician's questions, the patient's answers, the decision. */
export function itemConversation(item: ReviewItem, isPair: boolean) {
  return buildConversation({
    questions: item.clinicianQuestions ?? (item.clinicianQuestion ? [item.clinicianQuestion] : []),
    responses: item.patientResponses,
    decisions: item.outcome
      ? [{ at: item.outcome.decidedAt, label: outcomeActionLabel(item.outcome.action, isPair), reason: item.outcome.reason }]
      : [],
  });
}

type ScreenState =
  | { kind: "loading" }
  | { kind: "ready"; item: ReviewItem }
  | { kind: "outcome"; item: ReviewItem }
  | { kind: "success" }
  | { kind: "already-answered" }
  | { kind: "not-found" }
  | { kind: "load-failed" };

interface ReviewItemViewProps {
  itemId: string;
  /** Own dependent's id when acting as them; undefined for the patient's own items. */
  actingAsProfileId?: string;
  onBack: () => void;
  /** After the item changed server-side (sent, or found already answered): refetch records + count. */
  onItemChanged: () => void;
  /** "modal": shown inside the Medications tab's review dialog — no page
   *  chrome, and "Close" instead of "Back to medications". "stacked": one card
   *  of the bulk-review screen — laid out like the modal; an item that's
   *  answered elsewhere or gone just calls onBack (the card collapses), no toast. */
  variant?: "page" | "modal" | "stacked";
}

function StateCard({ icon, tone, message, onBack, backLabel = "Back to medications" }: {
  icon: string; tone: "success" | "neutral"; message: string; onBack: () => void; backLabel?: string;
}) {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center rounded-3xl border border-[#E5E7EB] bg-white px-6 py-12 text-center shadow-sm" role="status">
      <div className={cn("flex h-14 w-14 items-center justify-center rounded-full", tone === "success" ? "bg-[#ECFDF3]" : "bg-[#F3F4F6]")}>
        <Icon icon={icon} className={cn("h-7 w-7", tone === "success" ? "text-[#027A48]" : "text-[#9CA3AF]")} />
      </div>
      <p className="mt-4 text-base font-extrabold text-[#0A2947]">{message}</p>
      <Button onClick={onBack} className={cn(BRAND_BUTTON, "!mt-6")}>{backLabel}</Button>
    </div>
  );
}

const CORRECTED_FIELD_LABELS: Record<string, string> = {
  medication_name: "name", dose_quantity: "dose", dose_unit: "dose unit", frequency: "frequency",
  route: "route", dosage_text: "instructions", status: "status",
};

const EFFECT_PILL: Record<string, string> = {
  KEPT: "bg-[#ECFDF3] text-[#027A48]",
  CORRECTED: "bg-[#ECFDF3] text-[#027A48]",
  DISCONTINUED: "bg-[#F3F4F6] text-[#4B5563]",
  DISMISSED: "bg-[#FDECEF] text-[#E4002B]",
};

/** A resolved item (Module 5): the clinician's decision and what it did to each record. No answer form. */
function OutcomeView({ item, onBack }: { item: ReviewItem; onBack: () => void }) {
  const conflict = item.empi?.conflict;
  const meds = conflict ? [] : toMedications(item);
  const isPair = isPairItem(meds);
  const effectById = new Map(item.outcome?.effects.map((e) => [e.recordId, e.effect]) ?? []);
  // Corrected values are already in each record's fields; correctedFields says which ones.
  const correctedById = new Map(item.records.map((r) => [r.id, r.correctedFields ?? []]));
  return (
    <div className="mx-auto grid max-w-4xl gap-6 rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50">
          <Icon icon={isPair ? "lucide:git-compare" : "lucide:circle-help"} className="h-4 w-4 !text-white" />
        </div>
        <h2 className="text-lg font-extrabold text-[#0A2947]">{ISSUE_LABELS[item.triggerType]}</h2>
      </div>

      <section aria-label="Clinician's decision" className="rounded-2xl border border-[#027A48]/25 bg-[#ECFDF3] p-4">
        <div className="flex items-start gap-3">
          <Icon icon="lucide:badge-check" className="mt-0.5 h-5 w-5 shrink-0 text-[#027A48]" />
          <div>
            <p className="text-sm font-extrabold text-[#0A2947]">Clinician's decision</p>
            {item.outcome ? (
              <>
                <p className="mt-1 text-sm font-semibold text-[#0A2947]">{outcomeActionLabel(item.outcome.action, isPair)}</p>
                <p className="mt-1 text-sm text-[#374151]">"{item.outcome.reason}"</p>
                <p className="mt-1.5 text-xs text-[#6B7280]">{formatDate(item.outcome.decidedAt)}</p>
              </>
            ) : (
              <p className="mt-1 text-sm text-[#374151]">A clinician has reviewed this item.</p>
            )}
          </div>
        </div>
      </section>

      {conflict ? (
        <ConflictOptions
          detail={conflict}
          label="Your records"
          renderBadge={(option) => {
            const effect = effectById.get(option.recordId);
            return effect ? (
              <span data-testid="record-effect" className={cn("inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold", EFFECT_PILL[effect])}>
                {EFFECT_LABELS[effect]}
              </span>
            ) : null;
          }}
        />
      ) : <ReviewRecords
        records={meds}
        renderExtra={(med) => {
          const effect = effectById.get(med.id);
          const corrected = effect === "CORRECTED" ? correctedById.get(med.id) ?? [] : [];
          return effect ? (
            <>
              <p className="!mb-0 mt-3" data-testid="record-effect">
                <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold", EFFECT_PILL[effect])}>
                  {EFFECT_LABELS[effect]}
                </span>
              </p>
              {corrected.length > 0 && (
                <p className="!mb-0 mt-1 text-xs text-[#027A48]" data-testid="record-corrected-fields">
                  Corrected: {corrected.map((f) => CORRECTED_FIELD_LABELS[f] ?? f.replace(/_/g, " ")).join(", ")}
                </p>
              )}
            </>
          ) : null;
        }}
      />}

      {(item.patientResponses.length > 0 || (item.clinicianQuestions ?? []).length > 0) && (
        <PatientConversation messages={itemConversation(item, isPair)} viewer="patient" />
      )}

      <div className="flex justify-end">
        <Button onClick={onBack} className={BRAND_BUTTON}>Back to medications</Button>
      </div>
    </div>
  );
}

/** Loading placeholder with the review's layout: issue title, two record cards, answer choices, comment, buttons. */
function ReviewItemSkeleton() {
  return (
    <div className="grid gap-6" data-testid="review-item-skeleton" aria-busy="true" aria-label="Loading review">
      <div className="flex items-center gap-2.5">
        <Skeleton.Avatar active shape="square" size={36} className="[&_.ant-skeleton-avatar]:!rounded-xl" />
        <Skeleton.Input active size="small" className="!w-52" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {[0, 1].map((i) => (
          <div key={i} className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-4">
            <Skeleton active title={{ width: "40%" }} paragraph={{ rows: 4 }} />
          </div>
        ))}
      </div>
      <div>
        <Skeleton.Input active size="small" className="!mb-3 !w-72" />
        <div className="grid gap-3 sm:grid-cols-2">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton.Button key={i} active block className="[&_.ant-skeleton-button]:!h-[50px] [&_.ant-skeleton-button]:!rounded-2xl" />
          ))}
        </div>
      </div>
      <div>
        <Skeleton.Input active size="small" className="!mb-2 !w-44" />
        <Skeleton.Input active block className="[&_.ant-skeleton-input]:!h-24 [&_.ant-skeleton-input]:!rounded-lg" />
      </div>
      <div className="flex justify-end gap-3">
        <Skeleton.Button active className="[&_.ant-skeleton-button]:!h-10 [&_.ant-skeleton-button]:!w-48 [&_.ant-skeleton-button]:!rounded-lg" />
        <Skeleton.Button active className="[&_.ant-skeleton-button]:!h-10 [&_.ant-skeleton-button]:!w-24 [&_.ant-skeleton-button]:!rounded-lg" />
      </div>
    </div>
  );
}

export function ReviewItemView({ itemId, actingAsProfileId, onBack, onItemChanged, variant = "page" }: ReviewItemViewProps) {
  // The bulk screen's stacked card is laid out like the modal (no page chrome).
  const isModal = variant !== "page";
  const isStacked = variant === "stacked";
  const backLabel = isModal ? "Close" : "Back to medications";
  const [answer, setAnswer] = useState<ReviewAnswer | null>(null);
  const [comment, setComment] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  // Set when a send finds the item gone (404) — overrides whatever is cached.
  const [vanished, setVanished] = useState(false);

  // From the shared cache: opened from the queue, the row already loaded
  // there shows at once (no skeleton) while the full copy revalidates.
  const client = useQueryClient();
  // Bulk review shows one card per item, and the list it comes from already
  // holds each item in full and is what refreshes: a request per card was pure
  // duplication (connection plan step 4). A card whose item isn't in the list
  // still loads it on its own.
  const inList = Boolean(client.getQueryData(reviewListQuery(actingAsProfileId).queryKey)?.some((i) => i.id === itemId));
  const query = useQuery({
    ...reviewItemQuery(actingAsProfileId, itemId),
    placeholderData: () =>
      client.getQueryData(reviewListQuery(actingAsProfileId).queryKey)?.find((i) => i.id === itemId),
    enabled: variant !== "stacked" || !inList,
  });
  // The queue list already carries this item in full (same shape as the item
  // endpoint). If refreshing the item alone is slow or fails, the review keeps
  // working from that copy instead of failing; never fetched from here.
  const { data: listItem } = useQuery({
    ...reviewListQuery(actingAsProfileId),
    select: (items) => items.find((i) => i.id === itemId) ?? null,
    enabled: false,
  });
  const submit = useSubmitReviewResponse(actingAsProfileId);
  const deleteUpload = useDeleteRejectedUpload(actingAsProfileId);
  const uploadHistory = useOptionalUploadHistory();
  // "No, this isn't me" opens the delete popup straight away; nothing is sent
  // until the patient picks Delete or Keep hidden. The cross sends nothing.
  const [notMeOpen, setNotMeOpen] = useState(false);
  const [notMeAction, setNotMeAction] = useState<"keep" | "delete" | null>(null);
  // The not-me answer went through (a failed delete can then be retried or kept without resending).
  const notMeSent = useRef(false);
  // Set the moment Send/Skip is pressed (the mutation's own state lands a tick later).
  const [inFlight, setInFlight] = useState<ReviewAnswer | null>(null);
  const sending = inFlight !== null;
  const skipping = inFlight === "SKIPPED"; // which button gets the spinner

  // Set once the review is done and closing (answered here, or already
  // answered): the last form stays on screen while it closes, instead of the
  // optimistic update flashing an "already answered" card.
  const [closing, setClosing] = useState(false);
  const lastReady = useRef<ReviewItem | null>(null);

  const state = useMemo<ScreenState>(() => {
    // Also while the answer is sending: the optimistic update already adds it
    // to the item, which would pop the conversation card into the form mid-send.
    if ((closing || inFlight !== null) && lastReady.current) return { kind: "ready", item: lastReady.current };
    if (vanished) return { kind: "not-found" };
    // A bulk card follows the list (its fresh copy); elsewhere the item's own copy wins.
    const data = (variant === "stacked" ? listItem ?? query.data : query.data ?? listItem) ?? undefined;
    if (data) {
      if (data.itemStatus === "RESOLVED_CLINICIAN_VERIFIED") return { kind: "outcome", item: data };
      return (data.canRespond ?? data.awaitingInput) ? { kind: "ready", item: data } : { kind: "already-answered" };
    }
    if (query.isError) {
      return (query.error as { code?: string } | null)?.code === "REVIEW_ITEM_NOT_FOUND" ? { kind: "not-found" } : { kind: "load-failed" };
    }
    return { kind: "loading" };
  }, [closing, inFlight, vanished, variant, query.data, listItem, query.isError, query.error]);
  if (state.kind === "ready") lastReady.current = state.item;

  // In the modal, an item that's already answered (opened from a stale list,
  // or answered in another tab), gone, or failing to load isn't a screen of
  // its own: a toast says so and the modal closes.
  useEffect(() => {
    if (!isModal || closing) return;
    if (isStacked && state.kind !== "ready" && state.kind !== "loading" && state.kind !== "outcome") {
      // A bulk-review card: answered elsewhere or gone, it simply collapses.
      setClosing(true);
      onBack();
      return;
    }
    if (state.kind === "already-answered") {
      setClosing(true);
      toastSuccess(MESSAGES.alreadyAnswered);
      onItemChanged();
      onBack();
    } else if (state.kind === "load-failed" || state.kind === "not-found") {
      // A failed load isn't a screen of its own either: an error toast, and the modal closes.
      setClosing(true);
      toastError(state.kind === "not-found" ? MESSAGES.notFound : MESSAGES.loadFailed);
      if (state.kind === "not-found") onItemChanged();
      onBack();
    }
  }, [isModal, isStacked, closing, state.kind, onItemChanged, onBack]);

  const item = state.kind === "ready" ? state.item : null;
  const meds = useMemo(() => (item ? toMedications(item) : []), [item]);
  const isPair = isPairItem(meds);
  // Only what this item accepts (allowedAnswers — the set the backend enforces).
  const allowed = item?.allowedAnswers ?? [];
  const options = OPTIONS.filter((o) => allowed.includes(o) && (o !== "TAKE_BOTH" || isPair));
  // Nothing is required: Send is enabled as soon as an answer is chosen OR
  // something is typed (a message alone is sent as a plain REPLY). The
  // patient can come back and send more any time while the item is open.
  const replying = Boolean(item?.clinicianQuestion);
  const canSend = Boolean(answer) || comment.trim().length > 0;
  // Skip only exists before the item reaches a clinician.
  const canSkip = item?.itemStatus === "AWAITING_PATIENT" && allowed.includes("SKIPPED");

  // Sent straight away — no second "are you sure?" step. A skip goes on to the
  // clinician WITHOUT any comment, even if one was typed.
  // `extra` comes from the EMPI forms, which keep their own message and pick.
  const send = async (chosen: ReviewAnswer, extra?: { comment?: string; chosenRecordId?: string }): Promise<boolean> => {
    if (!item) return false;
    if (!extra && chosen === "REPLY" && !comment.trim()) {
      setFormError(MESSAGES.replyRequired);
      return false;
    }
    const trimmed = chosen === "SKIPPED" ? "" : extra ? extra.comment ?? "" : comment.trim();
    let submitted: ReviewItem | null;
    setInFlight(chosen);
    try {
      submitted = await submit.mutateAsync({
        itemId: item.id, answer: chosen, ...(trimmed ? { comment: trimmed } : {}),
        ...(extra?.chosenRecordId ? { chosenRecordId: extra.chosenRecordId } : {}),
      });
      setInFlight(null);
    } catch (caught) {
      setInFlight(null);
      const error = caught as { code?: string; message?: string };
      if (error.code === "ITEM_NOT_AWAITING_INPUT") {
        // Closed meanwhile (e.g. the clinician decided): say so and close the review.
        toastError(item.triggerType === "IDENTITY_MISMATCH" ? IDENTITY_MESSAGES.alreadyConfirmed : MESSAGES.alreadyReviewed);
        setClosing(true);
        // No onItemChanged(): the answer mutation's own onSettled already
        // reloaded records, medicines and the review list (calling it again
        // sent the same requests twice; connection plan step 4).
        onBack();
      } else if (error.code === "REVIEW_ITEM_NOT_FOUND") {
        setVanished(true);
      } else if (error.code === "VALIDATION_ERROR") {
        setFormError(error.message ?? null);
      } else {
        toastError(error.message ?? "Something went wrong. Please try again.");
      }
      return false;
    }
    if (chosen === "NOT_SAME_PERSON") {
      // Hidden now; the delete popup decides what comes next.
      setClosing(true);
      return true;
    }
    // Confirmed with a toast (no medicine names), then the review closes.
    toastSuccess(
      chosen === "SAME_PERSON"
        ? (submitted?.alsoConfirmed ? `${IDENTITY_MESSAGES.same} ${IDENTITY_MESSAGES.alsoConfirmed(submitted.alsoConfirmed)}` : IDENTITY_MESSAGES.same)
        : item.triggerType === "IDENTITY_MISMATCH" ? IDENTITY_MESSAGES.notSure
        : submitted?.itemStatus === "RESOLVED_PATIENT_REPORTED" ? MESSAGES.resolvedNoLongerTake
        : chosen === "SKIPPED" ? MESSAGES.skipped
        : item.triggerType === "GOLDEN_RECORD_CONFLICT" ? CONFLICT_MESSAGES.sent
        : replying ? MESSAGES.replied
        : MESSAGES.success,
    );
    setClosing(true);
    // Reloaded by the answer mutation's onSettled already (see above).
    onBack();
    return true;
  };

  const finishAfterNotMe = (message: string) => {
    setNotMeOpen(false);
    toastSuccess(message);
    // The answer and the delete mutations' onSettled already reloaded (see above).
    onBack();
  };

  // Keep hidden / Delete upload: send "No, this isn't me" first (once), then delete if asked.
  const answerNotMe = async (action: "keep" | "delete") => {
    if (!item) return;
    setNotMeAction(action);
    try {
      if (!notMeSent.current) {
        if (!(await send("NOT_SAME_PERSON", {}))) return;
        notMeSent.current = true;
      }
      if (action === "keep") {
        finishAfterNotMe(IDENTITY_MESSAGES.notSame);
        return;
      }
      let deletion;
      try {
        deletion = await deleteUpload.mutateAsync(item.id);
      } catch (caught) {
        toastError((caught as { message?: string }).message ?? "Couldn't delete the upload. Please try again.");
        return;
      }
      // Gone from Recent Uploads and the Document Panel at once, not on the next page load.
      uploadHistory?.removeUpload(deletion.uploadId);
      finishAfterNotMe(IDENTITY_MESSAGES.deleted);
    } finally {
      setNotMeAction(null);
    }
  };

  // The cross: nothing is sent and the question stays open (unless the answer already went through).
  const dismissNotMe = () => {
    if (notMeAction) return;
    if (notMeSent.current) finishAfterNotMe(IDENTITY_MESSAGES.notSame);
    else setNotMeOpen(false);
  };

  const body = (() => {
    switch (state.kind) {
      case "loading":
        // In the modal: a skeleton shaped like the review, so the modal opens at about
        // its final size instead of a full-screen-tall spinner that then shrinks.
        return isModal ? <ReviewItemSkeleton /> : <PageSpinner className="bg-transparent" />;
      case "already-answered":
        return <StateCard icon="lucide:clipboard-check" tone="neutral" message={MESSAGES.alreadyAnswered} onBack={onBack} backLabel={backLabel} />;
      case "not-found":
        return <StateCard icon="lucide:file-x" tone="neutral" message={MESSAGES.notFound} onBack={onBack} backLabel={backLabel} />;
      case "load-failed":
        return <StateCard icon="lucide:wifi-off" tone="neutral" message={MESSAGES.loadFailed} onBack={onBack} backLabel={backLabel} />;
      case "outcome":
        return <OutcomeView item={state.item} onBack={onBack} />;
      case "ready":
        // Its records were removed (e.g. the uploads were deleted): nothing left to compare.
        if ((state.item.triggerType === "GOLDEN_RECORD_CONFLICT" && !state.item.empi?.conflict)
            || (state.item.triggerType === "IDENTITY_MISMATCH" && !state.item.empi?.identity)) {
          return <StateCard icon="lucide:file-x" tone="neutral" message={MESSAGES.notFound} onBack={onBack} backLabel={backLabel} />;
        }
        return null;
    }
  })();

  return (
    <div className={isModal ? "" : "min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5"}>
      {!isModal && <PageBackButton onClick={onBack} ariaLabel="Back to medications" className="!mb-5" />}
      {body ?? (item && (
        <div className={isModal ? "grid gap-6" : "mx-auto grid max-w-4xl gap-6 rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm"}>
          {/* b) Issue label */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50">
                <Icon icon={isPair ? "lucide:git-compare" : "lucide:circle-help"} className="h-4 w-4 !text-white" />
              </div>
              <h2 className="text-lg font-extrabold text-[#0A2947]">{ISSUE_LABELS[item.triggerType]}</h2>
            </div>
            {/* Only a clinician's question gets a pill; no "Under review" one. */}
            {item.itemStatus === "NEEDS_MORE_INFO" && <ClinicianQuestionBadge />}
          </div>

          {item.empi?.identity ? (
            <>
              <IdentityReview
                identity={item.empi.identity}
                allowedAnswers={allowed}
                sending={sending}
                onSubmit={(chosen) => void send(chosen, {})}
                onNotMe={() => setNotMeOpen(true)}
                notMeOpen={notMeOpen}
              />
              <AppModal
                open={notMeOpen}
                // No closing while the answer or the delete is being sent.
                closeDisabled={notMeAction !== null}
                onOpenChange={(open) => { if (!open) dismissNotMe(); }}
                title={<span className="text-[#0A2947]">{IDENTITY_MESSAGES.deleteTitle}</span>}
                description={<span className="text-[#4B5563]">{IDENTITY_MESSAGES.deleteBody}</span>}
                width={460}
                footer={
                  <div className="flex justify-end gap-3">
                    <Button
                      variant="outline"
                      disabled={notMeAction === "delete"}
                      loading={notMeAction === "keep"}
                      onClick={() => void answerNotMe("keep")}
                      className="!h-10 !rounded-lg !px-5 !text-sm !font-semibold"
                    >
                      {IDENTITY_MESSAGES.deleteKeep}
                    </Button>
                    <Button
                      disabled={notMeAction === "keep"}
                      loading={notMeAction === "delete"}
                      onClick={() => void answerNotMe("delete")}
                      className="!h-10 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-5 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white disabled:!cursor-not-allowed disabled:!opacity-60"
                    >
                      {IDENTITY_MESSAGES.deleteConfirm}
                    </Button>
                  </div>
                }
              />
            </>
          ) : item.empi?.conflict ? (
            <>
              {(item.patientResponses.length > 0 || (item.clinicianQuestions ?? []).length > 0 || item.clinicianQuestion) && (
                <PatientConversation
                  messages={itemConversation(item, false)}
                  viewer="patient"
                  waitingFor={item.clinicianQuestion ? "patient" : null}
                />
              )}
              <ConflictPicker
                conflict={item.empi.conflict}
                allowedAnswers={allowed}
                canSkip={item.itemStatus === "AWAITING_PATIENT"}
                inFlight={inFlight}
                onSubmit={(chosen, extra) => void send(chosen, extra)}
              />
            </>
          ) : (<>
          {/* Why we're asking: the same box as record and identity checks. */}
          {MEDICINE_REASON_TEXT[item.triggerType] && (
            <section className="rounded-xl border border-[#F8C9D2] bg-[#FFF5F7] p-4" aria-label="Why we're asking" data-testid="medicine-reason">
              <p className="!mb-0 text-sm font-semibold text-[#0A2947]">{MEDICINE_REASON_TEXT[item.triggerType]}</p>
              <p className="!mb-0 mt-1 flex items-start gap-1.5 text-sm text-[#4B5563]">
                <Icon icon="lucide:stethoscope" className="mt-0.5 h-4 w-4 shrink-0 text-[#0A2947]" />
                Your clinician makes the final decision.
              </p>
            </section>
          )}

          {/* c) Records */}
          <ReviewRecords records={meds} />

          {/* Patient Conversation: the clinician's questions and your earlier answers */}
          {(item.patientResponses.length > 0 || (item.clinicianQuestions ?? []).length > 0 || item.clinicianQuestion) && (
            <PatientConversation
              messages={itemConversation(item, isPair)}
              viewer="patient"
              waitingFor={item.clinicianQuestion ? "patient" : null}
            />
          )}

          {/* d) Question */}
          <section>
            <p id="review-question" className="mb-3 text-sm font-extrabold text-[#0A2947]">
              Which of these best describes this medicine?

            </p>
            <div role="radiogroup" aria-labelledby="review-question" className="grid gap-3 sm:grid-cols-2">
              {options.map((option) => {
                const selected = answer === option;
                return (
                  <Button
                    key={option}
                    variant="outline"
                    role="radio"
                    aria-checked={selected}
                    // Picking the selected choice again clears it — nothing is required.
                    onClick={() => { setAnswer(selected ? null : option); setFormError(null); }}
                    className={cn(
                      "!flex !h-auto !w-full !items-center !justify-start !gap-3 !whitespace-normal !rounded-2xl !px-4 !py-3.5 !text-left !text-sm !font-semibold !text-[#0A2947]",
                      selected
                        ? "!border-2 !border-[#F17893] !bg-[#FFF2F4] hover:!border-[#F17893] hover:!bg-[#FFF2F4] hover:!text-[#0A2947]"
                        : "!border-[#DDE1E8] !bg-white hover:!border-[#F17893]/50 hover:!bg-white hover:!text-[#0A2947]",
                    )}
                  >
                    <Icon
                      icon={selected ? "lucide:circle-dot" : "lucide:circle"}
                      className={cn("h-4 w-4 shrink-0", selected ? "text-[#E4002B]" : "text-[#9CA3AF]")}
                    />
                    <span>
                      {ANSWER_LABELS[option]}
                      {option === "NO_LONGER_TAKE" && (
                        <span className="block text-xs font-normal text-[#6B7280]">Closes this review right away</span>
                      )}
                    </span>
                  </Button>
                );
              })}
            </div>
          </section>

          {/* e) Message to the clinician — free text, any time */}
          <section>
            <label htmlFor="review-comment" className="mb-2 block text-sm font-semibold text-[#0A2947]">
              {replying ? "Your reply to your clinician" : "Message to your clinician"}
            </label>
            <Textarea
              id="review-comment"
              value={comment}
              onChange={(e) => { setComment(e.target.value.slice(0, COMMENT_MAX_LENGTH)); setFormError(null); }}
              maxLength={COMMENT_MAX_LENGTH}
              showCount
              rows={4}
              placeholder={replying ? "Type your answer to the question above" : "Anything your clinician should know"}
              className="!rounded-lg"
            />
            <p className="!mb-0 mt-1.5 text-xs text-[#9CA3AF]">Choose an answer, write a message, or both.</p>
          </section>

          {formError && <p role="alert" className="-mt-3 text-xs text-[#E4002B]">{formError}</p>}

          {/* f) Actions */}
          <div className="flex flex-wrap items-center justify-end gap-3">
            {/* Skip: on to the clinician as it is — no answer, no comment. Only in the patient's own window. */}
            {canSkip && <Button
              variant="ghost"
              disabled={sending && !skipping}
              loading={sending && skipping}
              onClick={() => void send("SKIPPED")}
              className="!h-10 !rounded-lg !px-4 !text-sm !font-semibold !text-[#4B5563] hover:!bg-[#F3F4F6] hover:!text-[#E4002B]"
            >
              {ACTION_LABELS.skip}
            </Button>}
            <Button
              disabled={!canSend || (sending && skipping)}
              loading={sending && !skipping}
              onClick={() => void send(answer ?? "REPLY")}
              className={BRAND_BUTTON}
            >
              {ACTION_LABELS.send}
            </Button>
          </div>
          </>)}
        </div>
      ))}

    </div>
  );
}
